import { useToast } from "@/hooks/useToast";
import { formatToolsForAssistant } from "@/lib/utils/ai/toolFormatting";
import { useAssistantStore } from "@/store";
import PropTypes from "prop-types";
import { AssistantCreationChat } from "./AssistantCreationChat";

const AssistantCreationFlow = ({
  assistant,
  onCancel,
  onCreationComplete,
  onStreamingUpdate,
}) => {
  const { toast } = useToast();
  const { updateAssistant } = useAssistantStore();

  // Handle streaming updates from the chat component
  const handleStreamingUpdate = (update) => {
    if (!onStreamingUpdate) return;

    if (update.type === "switch_to_edit") {
      // Signal parent to switch to edit mode with initial name
      onStreamingUpdate({
        type: "switch_to_edit",
        assistantId: assistant?.id,
        name: update.name,
        prompt: update.prompt,
      });
    } else if (update.type === "field_update") {
      // Pass field updates to parent for form synchronization
      onStreamingUpdate({
        type: "field_update",
        assistantId: assistant?.id,
        fields: update.fields,
        isComplete: update.isComplete,
      });
    }
  };

  // Handle when the assistant details are generated from the chat
  const handleAssistantGenerated = async (generatedData) => {
    if (!assistant || !assistant.id) {
      toast({
        title: "Error",
        description: "Cannot update assistant: missing assistant data",
        variant: "destructive",
      });
      return;
    }

    try {
      // Convert tool names to actual tool objects with proper structure
      const formattedTools = formatToolsForAssistant(generatedData.tools);

      // Update the assistant with generated data, including the model recommendation
      await updateAssistant(assistant.id, {
        name: generatedData.name,
        description: generatedData.description,
        instructions: generatedData.instructions,
        tools: formattedTools,
        model: generatedData.model || "gpt-4.1-mini", // Use the recommended model or fallback
        metadata: {
          ...assistant.metadata,
          generated_from_prompt: "true",
          generation_prompt: generatedData.prompt,
          recommended_tools: JSON.stringify(generatedData.tools),
          reasoning: generatedData.reasoning,
          primary_purpose: generatedData.primary_purpose || "general",
          updated_at: new Date().toISOString(),
        },
      });

      // Create updated assistant object
      const updatedAssistant = {
        ...assistant,
        name: generatedData.name,
        description: generatedData.description,
        instructions: generatedData.instructions,
        tools: formattedTools,
        model: generatedData.model || "gpt-4.1-mini",
        metadata: {
          ...assistant.metadata,
          generated_from_prompt: "true",
          generation_prompt: generatedData.prompt,
          recommended_tools: JSON.stringify(generatedData.tools),
          reasoning: generatedData.reasoning,
          primary_purpose: generatedData.primary_purpose || "general",
        },
      };

      // Notify parent that creation is complete - parent will switch to edit mode
      if (onCreationComplete) {
        onCreationComplete(updatedAssistant);
      }
    } catch (error) {
      console.error("Error updating assistant:", error);
      toast({
        title: "Update Failed",
        description:
          error.message || "Failed to update assistant with generated data",
        variant: "destructive",
      });
    }
  };

  // Show the chat input - parent handles switching to edit mode after creation
  return (
    <AssistantCreationChat
      onAssistantGenerated={handleAssistantGenerated}
      onStreamingUpdate={onStreamingUpdate ? handleStreamingUpdate : undefined}
      onCancel={onCancel}
    />
  );
};

AssistantCreationFlow.propTypes = {
  assistant: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onCreationComplete: PropTypes.func,
  onStreamingUpdate: PropTypes.func,
};

export default AssistantCreationFlow;
